// src/pages/events/ResultsDialog.jsx
import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Chip, Divider, IconButton, Tooltip, Paper,
  TextField, InputAdornment, Tabs, Tab, List, ListItem, ListItemText, Stack,
  Switch, FormControlLabel
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';
import GroupIcon from '@mui/icons-material/Group';
import { onValue, ref, off, update } from 'firebase/database';
import { db } from '../../firebase';

function useEmployeesMap() {
  const [emps, setEmps] = useState({});
  useEffect(() => {
    const r = ref(db, '/employees');
    const cb = (s) => setEmps(s.val() || {});
    onValue(r, cb);
    return () => off(r, 'value', cb);
  }, []);

  const infoOf = (uid) => {
    const e = emps?.[uid];
    if (!e) return { name: uid, email: '' };
    const name =
      e.submittedProfile?.profileFields?.fullName ||
      e.fullName ||
      uid;
    const email =
      e.submittedProfile?.profileFields?.email ||
      e.email ||
      '';
    return { name, email };
  };

  return { infoOf, emps };
}

function normalizeLabel(k) {
  if (!k) return '';
  const l = String(k).trim().toLowerCase();
  if (l === 'behaviour' || l === 'behavior') return 'Behaviour';
  if (l === 'collaboration') return 'Collaboration';
  if (l === 'reliability') return 'Reliability';
  if (l === 'initiative') return 'Initiative';
  return k;
}

/** seconds.milliseconds (2+3 digits) e.g., "12.345" ; shows "00.000" if missing */
function fmtSecMs(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n < 0) return '00.000';
  const sec = Math.floor(n / 1000);
  const mmm = Math.floor(n % 1000);
  return `${String(sec).padStart(2, '0')}.${String(mmm).padStart(3, '0')}`;
}

export default function ResultsDialog({ open, onClose, event }) {
  const [raw, setRaw] = useState(null);
  const [quizRaw, setQuizRaw] = useState(null);
  const [attendance, setAttendance] = useState({});
  const [performanceIndex, setPerformanceIndex] = useState({});
  const { infoOf, emps } = useEmployeesMap();

  const isQuiz = event?.type === 'QUESTIONNAIRE' && !event?.peerReview?.enabled;

  useEffect(() => {
    if (!open || !event?.id) {
      setRaw(null);
      setQuizRaw(null);
      setAttendance({});
      setPerformanceIndex({});
      return;
    }

    // Attendance for peer review results (0–4 points per user)
    const attRef = ref(db, `/events/${event.id}/attendance`);
    const attCb = (s) => setAttendance(s.val() || {});
    onValue(attRef, attCb);

    // Performance Index for peer review results (0–5 points per user)
    const piRef = ref(db, `/events/${event.id}/performanceIndex`);
    const piCb = (s) => setPerformanceIndex(s.val() || {});
    onValue(piRef, piCb);

    if (isQuiz) {
      const qRef = ref(db, `/eventStatusByUser/${event.id}/standard`);
      const qCb = (s) => setQuizRaw(s.val() || {});
      onValue(qRef, qCb);
      return () => {
        off(qRef, 'value', qCb);
        off(attRef, 'value', attCb);
        off(piRef, 'value', piCb);
      };
    }

    const pRef = ref(db, `/eventStatusByUser/${event.id}/performance`);
    const pCb = (snap) => setRaw({ data: snap.val() || {}, source: 'eventScoped' });
    onValue(pRef, pCb);

    return () => {
      off(pRef, 'value', pCb);
      off(attRef, 'value', attCb);
      off(piRef, 'value', piCb);
    };
  }, [open, event?.id, isQuiz]);

  /* -------------------- QUIZ HELPERS -------------------- */
  const getSelectedIndex = (answer, options) => {
    if (Number.isInteger(answer) && answer >= 0 && answer < (options?.length || 0)) return answer;
    const idx = (options || []).findIndex(o => o === answer);
    return idx >= 0 ? idx : null;
  };
  const getCorrectIndex = (q) => {
    const options = q?.options || [];
    if (Number.isInteger(q?.correctAnswerIndex) && q.correctAnswerIndex >= 0 && q.correctAnswerIndex < options.length) {
      return q.correctAnswerIndex;
    }
    if (Number.isInteger(q?.correctAnswer) && q.correctAnswer >= 0 && q.correctAnswer < options.length) {
      return q.correctAnswer;
    }
    if (q?.correctAnswer != null) {
      const i = options.findIndex(o => o === q.correctAnswer);
      if (i >= 0) return i;
    }
    return null;
  };

  /* -------------------- QUIZ DISTRIBUTION (existing) -------------------- */
  const quizResults = useMemo(() => {
    if (!isQuiz || !quizRaw) return null;

    const questions = event?.questionnaire?.questions || [];
    const submissions = Object.values(quizRaw).filter(v => v?.completed && v?.answers);
    const total = submissions.length;
    if (!total) return [];

    return questions.map((q) => {
      if (q.type === 'MCQ') {
        const options = q.options || [];
        const counts = Array(options.length).fill(0);

        submissions.forEach(sub => {
          const ans = sub.answers?.[q.id];
          const idx = getSelectedIndex(ans, options);
          if (idx != null && idx >= 0 && idx < options.length) counts[idx] += 1;
        });

        return { ...q, counts, total };
      }

      if (q.type === 'STAR') {
        const vals = submissions
          .map(s => Number(s.answers?.[q.id]) || 0)
          .filter(v => v > 0);
        const avg = vals.length ? vals.reduce((a,b)=>a+b,0)/vals.length : 0;
        return { ...q, avg, count: vals.length };
      }

      if (q.type === 'TEXT') {
        const texts = submissions.map(s => s.answers?.[q.id]).filter(Boolean);
        return { ...q, texts };
      }

      return q;
    });
  }, [quizRaw, event, isQuiz]);

  /* -------------------- QUIZ SCORING (weighted + optional negative) + TIME FACTOR -------------------- */
  const {
    leaderboard,
    perQuestionPicks,
    negativeMarking,
    totalPossibleQuiz,
  } = useMemo(() => {
    const ret = {
      leaderboard: [],
      perQuestionPicks: [],
      negativeMarking: !!event?.questionnaire?.negativeMarking,
      totalPossibleQuiz: 0,
    };
    if (!isQuiz || !quizRaw) return ret;

    const questions = (event?.questionnaire?.questions || []).filter(q => q?.type === 'MCQ');
    if (questions.length === 0) return ret;

    const submissionsObj = quizRaw || {};
    const submissions = Object.entries(submissionsObj)
      .map(([uid, rec]) => ({ uid, ...rec }))
      .filter(v => v?.completed && v?.answers);

    const totalPossible = questions.reduce((acc, q) => acc + (Number(q?.weight) || 0), 0);
    ret.totalPossibleQuiz = totalPossible;

    const perQ = questions.map(q => {
      const correctIdx = getCorrectIndex(q);
      const options = (q.options || []).map((text, idx) => ({
        text,
        isCorrect: (correctIdx != null && idx === correctIdx),
        choosers: [],
      }));
      return { qId: q.id, label: q.label, options };
    });

    const board = submissions.map(({ uid, answers, submittedAt, timeUsedMs }) => {
      const { name, email } = infoOf(uid);
      let gained = 0;
      let penalty = 0;
      let correct = 0;
      let wrong = 0;
      let unanswered = 0;

      questions.forEach(q => {
        const weight = Number(q?.weight) || 0;
        const options = q?.options || [];
        const selectedIdx = getSelectedIndex(answers?.[q.id], options);
        const correctIdx = getCorrectIndex(q);

        const perQRow = perQ.find(p => p.qId === q.id);
        if (selectedIdx != null && perQRow && perQRow.options[selectedIdx]) {
          perQRow.options[selectedIdx].choosers.push({
            uid,
            name,
            email,
            selectedCorrect: (correctIdx != null && selectedIdx === correctIdx)
          });
        }

        if (selectedIdx == null || correctIdx == null) {
          if (selectedIdx == null) unanswered += 1;
          return;
        }

        if (selectedIdx === correctIdx) {
          gained += weight;
          correct += 1;
        } else {
          wrong += 1;
          if (ret.negativeMarking) penalty += weight / 4;
        }
      });

      const score = gained - penalty;
      const pctRaw = totalPossible > 0 ? (score / totalPossible) * 100 : 0;
      const pct = Math.max(0, pctRaw);

      const tUsed = Number(timeUsedMs);
      const time = Number.isFinite(tUsed) ? tUsed : null; // may be null for older attempts
      const submitted = Number(submittedAt) || 0;

      return {
        uid, name, email,
        gained,
        penalty,
        score,
        totalPossible,
        pct,
        correct, wrong, unanswered,
        timeUsedMs: time,
        submittedAt: submitted,
      };
    });

    // Sort: Score/% desc, then timeUsedMs asc (faster wins), then submittedAt asc, then name
    board.sort((a, b) => {
      const byPct = b.pct - a.pct;
      if (byPct !== 0) return byPct;

      // handle missing time: treat null/undefined as Infinity (go after timed ones)
      const ta = (a.timeUsedMs == null) ? Number.POSITIVE_INFINITY : a.timeUsedMs;
      const tb = (b.timeUsedMs == null) ? Number.POSITIVE_INFINITY : b.timeUsedMs;
      if (ta !== tb) return ta - tb;

      if (a.submittedAt !== b.submittedAt) return a.submittedAt - b.submittedAt;
      return a.name.localeCompare(b.name);
    });

    // Assign shared ranks when BOTH score and time match (classic competition ranking: 1,1,1,4)
    let prev = null;
    let prevRank = 0;
    board.forEach((row, i) => {
      if (
        prev &&
        Number(prev.score).toFixed(6) === Number(row.score).toFixed(6) &&
        ((prev.timeUsedMs ?? -1) === (row.timeUsedMs ?? -1))
      ) {
        row.rank = prevRank;
      } else {
        row.rank = i + 1;
        prevRank = row.rank;
        prev = row;
      }
    });

    // Sort choosers for activity panel
    perQ.forEach(qr => {
      qr.options.forEach(opt => {
        opt.choosers.sort((a, b) => a.name.localeCompare(b.name));
      });
    });

    ret.leaderboard = board;
    ret.perQuestionPicks = perQ;
    return ret;
  }, [isQuiz, quizRaw, event, infoOf]);

  /* -------------------- PEER REVIEW (STAR + MICRO aggregation) -------------------- */
  const starLabels = useMemo(() => {
    const schema =
      event?.peerReview?.schema ||
      event?.questionnaire?.questions ||
      [];
    const labels = schema
      .filter(q => q?.type === 'STAR')
      .map(q => normalizeLabel(q?.label || q?.id));
    const fallback = ['Behaviour', 'Collaboration', 'Reliability', 'Initiative'];
    const chosen = labels.length ? labels : fallback;
    return Array.from(new Set(chosen));
  }, [event]);

  const summary = useMemo(() => {
    if (!raw?.data) return null;

    const aggStars = {};
    const reviewersByTarget = {};
    const microAgg = {};

    Object.entries(raw.data).forEach(([reviewerUid, rec]) => {
      const answers = rec?.answers || {};
      const micro = rec?.micro || {};

      Object.entries(answers).forEach(([targetUid, qmap]) => {
        Object.entries(qmap || {}).forEach(([qid, val]) => {
          if (typeof val === 'number' && isFinite(val)) {
            const label = normalizeLabel(qid);
            aggStars[targetUid] ||= {};
            aggStars[targetUid][label] ||= { sum: 0, count: 0 };
            aggStars[targetUid][label].sum += val;
            aggStars[targetUid][label].count += 1;

            reviewersByTarget[targetUid] ||= new Set();
            reviewersByTarget[targetUid].add(reviewerUid);
          }
        });
      });

      Object.entries(micro).forEach(([targetUid, v]) => {
        const num = Number(v);
        if (!Number.isFinite(num)) return;
        microAgg[targetUid] ||= { sum: 0, count: 0 };
        microAgg[targetUid].sum += num;
        microAgg[targetUid].count += 1;
      });
    });

    const rows = Object.keys({ ...aggStars, ...microAgg }).map((targetUid) => {
      const perQStats = aggStars[targetUid] || {};
      const perQAvg = {};
      let presentLabels = 0;
      let sumOfAverages = 0;

      starLabels.forEach(label => {
        const st = perQStats[label];
        const avg = st && st.count ? (st.sum / st.count) : 0;
        perQAvg[label] = avg;
        if (st && st.count) {
          presentLabels += 1;
          sumOfAverages += avg;
        }
      });

      const overall = presentLabels ? (sumOfAverages / presentLabels) : 0;

      const mAgg = microAgg[targetUid] || { sum: 0, count: 0 };
      const microAvg = mAgg.count ? (mAgg.sum / mAgg.count) : 0;
      const microAvgFixed = microAvg > 0 ? Number(microAvg.toFixed(3)) : 0;

      const reviewers = (reviewersByTarget[targetUid] || new Set()).size;
      const info = infoOf(targetUid);

      return {
        targetUid,
        ...info,
        perQAvg,
        overall,
        reviewers,
        microAvg: microAvgFixed,
        microVotes: mAgg.count,
      };
    });

    rows.sort((a, b) =>
      (b.overall - a.overall) ||
      (b.microAvg - a.microAvg) ||
      a.name.localeCompare(b.name)
    );

    return rows;
  }, [raw, infoOf, starLabels]);

  // ---- Combine Attendance (0–4), Micro (0–1), Attributes (0–5), Performance (0–5) -> TOTAL /15 ----
  const {
    eligibleRows,
    ineligibleRows,
    hasAnyPeerData
  } = useMemo(() => {
    if (isQuiz) {
      return { eligibleRows: [], ineligibleRows: [], hasAnyPeerData: false };
    }
    const rows = summary || [];
    if (!rows.length) return { eligibleRows: [], ineligibleRows: [], hasAnyPeerData: false };

    const ELIGIBILITY_ATTR_MIN = 3.0;
    const ELIGIBILITY_LAST5_MIN = 3.0;
    const ELIGIBILITY_PERF_MIN = 3.0;

    const withTotals = rows.map(r => {
      const attendanceRaw = Number(attendance?.[r.targetUid]);
      const attendancePts = Number.isFinite(attendanceRaw) ? Math.max(0, Math.min(4, attendanceRaw)) : 0;
      const microPts = Math.max(0, Math.min(1, Number(r.microAvg) || 0));
      const attrPts = Math.max(0, Math.min(5, Number(r.overall) || 0));
      const perfRaw = Number(performanceIndex?.[r.targetUid]);
      const performancePts = Number.isFinite(perfRaw) ? Math.max(0, Math.min(5, perfRaw)) : 0;

      const failedAttributes = [];
      (starLabels || []).forEach(lbl => {
        const v = Number(r.perQAvg?.[lbl]) || 0;
        if (v < ELIGIBILITY_ATTR_MIN) failedAttributes.push(lbl);
      });
      const passAttributes = failedAttributes.length === 0;

      const last5 = attendancePts + microPts;
      const passLast5 = last5 >= ELIGIBILITY_LAST5_MIN;

      const passPerformance = performancePts >= ELIGIBILITY_PERF_MIN;

      const total15 = attrPts + attendancePts + microPts + performancePts;

      return {
        ...r,
        attendancePts,
        microPts,
        attrPts,
        performancePts,
        last5,
        total15,
        passAttributes,
        failedAttributes,
        passLast5,
        passPerformance,
        eligible: passAttributes && passLast5 && passPerformance
      };
    });

    const eligible = withTotals
      .filter(x => x.eligible)
      .sort((a, b) =>
        (b.total15 - a.total15) ||
        (b.attrPts - a.attrPts) ||
        (b.microPts - a.microPts) ||
        (b.attendancePts - a.attendancePts) ||
        (b.performancePts - a.performancePts) ||
        a.name.localeCompare(b.name)
      );

    const ineligible = withTotals
      .filter(x => !x.eligible)
      .sort((a, b) => a.name.localeCompare(b.name));

    return { eligibleRows: eligible, ineligibleRows: ineligible, hasAnyPeerData: withTotals.length > 0 };
  }, [summary, attendance, performanceIndex, starLabels, isQuiz]);

  /* -------------------- Reviewer summaries -------------------- */
  const { reviewerSummaries, givenByReviewer } = useMemo(() => {
    const result = { reviewerSummaries: [], givenByReviewer: {} };
    if (!raw?.data) return result;

    const summaries = {};
    const grouped = {};

    Object.entries(raw.data).forEach(([reviewerUid, rec]) => {
      const reviewerInfo = infoOf(reviewerUid);
      const answers = rec?.answers || {};

      Object.entries(answers).forEach(([targetUid, qmap]) => {
        const perQ = {};
        let present = 0;
        let sum = 0;

        (starLabels || []).forEach(lbl => {
          const direct = qmap?.[lbl];
          const lower = qmap?.[lbl?.toLowerCase?.()];
          const normalizedFallback = qmap?.[Object.keys(qmap).find(k => normalizeLabel(k) === lbl)];
          const val = direct ?? lower ?? normalizedFallback ?? null;
          const num = (typeof val === 'number' && isFinite(val)) ? val : null;
          perQ[lbl] = num;
          if (num != null) { present += 1; sum += num; }
        });

        const overallGiven = present ? (sum / present) : 0;
        const targetInfo = infoOf(targetUid);

        grouped[reviewerUid] ||= [];
        grouped[reviewerUid].push({
          reviewerUid,
          targetUid,
          targetName: targetInfo.name,
          perQ,
          overallGiven
        });

        summaries[reviewerUid] ||= { reviewerUid, name: reviewerInfo.name, email: reviewerInfo.email, count: 0, sumOverall: 0 };
        summaries[reviewerUid].count += 1;
        summaries[reviewerUid].sumOverall += overallGiven;
      });
    });

    Object.keys(grouped).forEach(uid => {
      grouped[uid].sort((a, b) => a.targetName.localeCompare(b.targetName));
    });

    const summaryArr = Object.values(summaries).map(s => ({
      reviewerUid: s.reviewerUid,
      name: s.name,
      email: s.email,
      count: s.count,
      avgGiven: s.count ? (s.sumOverall / s.count) : 0
    })).sort((a, b) => (b.count - a.count) || a.name.localeCompare(b.name));

    result.reviewerSummaries = summaryArr;
    result.givenByReviewer = grouped;
    return result;
  }, [raw, infoOf, starLabels]);

  // --- participation analysis (peer-review only) ---
  const allActiveCompletedUids = useMemo(() => {
    return Object.entries(emps || {})
      .filter(([, e]) => (e?.active !== false) && e?.onboardingStatus === 'COMPLETED')
      .map(([uid]) => uid);
  }, [emps]);

  const expectedReviewers = useMemo(() => {
    const aud = event?.audience;
    if (!aud) return [];
    if (aud.all) return allActiveCompletedUids;
    return (aud.selected || []).filter(Boolean);
  }, [event?.audience, allActiveCompletedUids]);

  const expectedTargets = useMemo(() => {
    if (event?.peerReview?.enabled) {
      return (event?.peerReview?.targets || []).filter(Boolean);
    }
    return [];
  }, [event]);

  const reviewersSet = useMemo(() => new Set(Object.keys(raw?.data || {})), [raw]);
  const reviewedList = useMemo(() => expectedReviewers.filter(uid => reviewersSet.has(uid)), [expectedReviewers, reviewersSet]);
  const notParticipatedList = useMemo(() => expectedReviewers.filter(uid => !reviewersSet.has(uid)), [expectedReviewers, reviewersSet]);

  const partialList = useMemo(() => {
    if (!event?.peerReview?.enabled || expectedTargets.length === 0) return [];
    return reviewedList.filter(uid => {
      const answers = raw?.data?.[uid]?.answers || {};
      const ratedCount = expectedTargets.filter(t => answers[t]).length;
      return ratedCount < expectedTargets.length;
    });
  }, [reviewedList, raw, expectedTargets, event?.peerReview?.enabled]);

  // --- UI state ---
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpanded = (uid) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const [search, setSearch] = useState('');
  const filteredReviewerSummaries = useMemo(() => {
    if (!search.trim()) return reviewerSummaries;
    const q = search.trim().toLowerCase();
    return reviewerSummaries.filter(rev => {
      const baseMatch =
        rev.name.toLowerCase().includes(q) ||
        (rev.email || '').toLowerCase().includes(q);
      if (baseMatch) return true;
      const rows = givenByReviewer[rev.reviewerUid] || [];
      return rows.some(r => (r.targetName || '').toLowerCase().includes(q));
    });
  }, [search, reviewerSummaries, givenByReviewer]);

  const [participationOpen, setParticipationOpen] = useState(false);
  const [participationTab, setParticipationTab] = useState(0);

  const [showActivity, setShowActivity] = useState(false);

  const [eligibilityTab, setEligibilityTab] = useState(0);

  const [confirmDeclareOpen, setConfirmDeclareOpen] = useState(false);
  const [confirmWorking, setConfirmWorking] = useState(false);

  const windowText = event?.dates
    ? `Window: ${new Date(event.dates.start).toLocaleString()} → ${new Date(event.dates.end).toLocaleString()}`
    : '—';

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          Results — {event?.title || 'Questionnaire'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', mb: 2 }}>
            <Typography color="text.secondary">{windowText}</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              {!isQuiz && (
                <Button
                  size="small"
                  startIcon={<GroupIcon />}
                  variant="outlined"
                  onClick={() => setParticipationOpen(true)}
                >
                  Participation
                </Button>
              )}

              <FormControlLabel
                control={
                  <Switch
                    checked={!!event?.results?.declared}
                    onChange={async (e) => {
                      const next = !!e.target.checked;
                      try {
                        if (next) {
                          setConfirmDeclareOpen(true);
                        } else {
                          await update(ref(db, `/events/${event.id}/results`), {
                            declared: false,
                            declaredAt: null,
                          });
                        }
                      } catch (err) {
                        console.error('Failed to toggle results.declared', err);
                      }
                    }}
                  />
                }
                label={!!event?.results?.declared ? 'Results declared' : 'Declare results'}
              />
            </Stack>
          </Box>

          {/* ===== QUIZ LEADERBOARD ===== */}
          {isQuiz && leaderboard && leaderboard.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb: 1 }}>
                <Typography variant="h6">Leaderboard</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    size="small"
                    label={negativeMarking ? 'Negative marking ON (−¼ weight for wrong)' : 'Negative marking OFF'}
                    color={negativeMarking ? 'warning' : 'default'}
                  />
                  <Chip size="small" label={`Total Marks: ${totalPossibleQuiz}`} />
                </Stack>
              </Box>

              <Box component="table" sx={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign:'left', padding:'8px 6px' }}>Rank</th>
                    <th style={{ textAlign:'left', padding:'8px 6px' }}>Employee</th>
                    <th style={{ textAlign:'left', padding:'8px 6px' }}>Score</th>
                    <th style={{ textAlign:'left', padding:'8px 6px' }}>%</th>
                    <th style={{ textAlign:'left', padding:'8px 6px' }}>Time</th>{/* NEW */}
                    <th style={{ textAlign:'left', padding:'8px 6px' }}>Correct</th>
                    <th style={{ textAlign:'left', padding:'8px 6px' }}>Wrong</th>
                    <th style={{ textAlign:'left', padding:'8px 6px' }}>Unanswered</th>
                    <th style={{ textAlign:'left', padding:'8px 6px' }}>Gained</th>
                    <th style={{ textAlign:'left', padding:'8px 6px' }}>Penalty</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((row) => (
                    <tr key={row.uid}>
                      <td style={{ padding:'8px 6px', width: 70 }}>
                        <Chip label={`#${row.rank}`} size="small" />{/* NEW rank with ties */}
                      </td>
                      <td style={{ padding:'8px 6px' }}>
                        <Typography sx={{ fontWeight: 600 }}>{row.name}</Typography>
                        {row.email && (
                          <Typography variant="caption" color="text.secondary">
                            {row.email}
                          </Typography>
                        )}
                      </td>
                      <td style={{ padding:'8px 6px' }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            size="small"
                            color={row.score < 0 ? 'error' : 'primary'}
                            label={`${row.score.toFixed(3)} / ${row.totalPossible}`}
                            sx={{ fontWeight: 600 }}
                          />
                          {row.score < 0 && (
                            <Chip
                              size="small"
                              color="error"
                              variant="outlined"
                              label={`Negative by ${Math.abs(row.score).toFixed(3)}`}
                            />
                          )}
                        </Stack>
                      </td>
                      <td style={{ padding:'8px 6px' }}>
                        <Chip
                          size="small"
                          color="success"
                          label={`${row.pct.toFixed(3)}%`}
                        />
                      </td>
                      <td style={{ padding:'8px 6px' }}>
                        {row.timeUsedMs == null
                          ? <Typography variant="body2" color="text.secondary">—</Typography>
                          : <Chip size="small" label={fmtSecMs(row.timeUsedMs)} variant="outlined" />}
                      </td>
                      <td style={{ padding:'8px 6px' }}>{row.correct}</td>
                      <td style={{ padding:'8px 6px' }}>{row.wrong}</td>
                      <td style={{ padding:'8px 6px' }}>{row.unanswered}</td>
                      <td style={{ padding:'8px 6px' }}>
                        <Chip size="small" color="success" variant="outlined" label={`+${row.gained.toFixed(3)}`} />
                      </td>
                      <td style={{ padding:'8px 6px' }}>
                        <Chip size="small" color="error" variant="outlined" label={`−${row.penalty.toFixed(3)}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Box>

              <Box sx={{ display:'flex', justifyContent:'flex-end', mt: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setShowActivity(v => !v)}
                  startIcon={showActivity ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
                >
                  {showActivity ? 'Hide Answer Activity' : 'Show Answer Activity'}
                </Button>
              </Box>

              {showActivity && (
                <Box sx={{ mt: 2 }}>
                  {perQuestionPicks.map((q, qi) => (
                    <Paper key={q.qId} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                      <Typography sx={{ fontWeight: 700, mb: 1 }}>
                        Q{qi + 1}. {q.label}
                      </Typography>
                      {(q.options || []).map((opt, oi) => {
                        const count = opt.choosers.length;
                        return (
                          <Box key={oi} sx={{ mb: 1.25 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {opt.text || `Option ${oi + 1}`} {opt.isCorrect && <Chip size="small" label="Correct" color="success" sx={{ ml: 1 }} />}
                              <Chip size="small" label={`${count} selected`} sx={{ ml: 1 }} />
                            </Typography>
                            {count === 0 ? (
                              <Typography variant="caption" color="text.secondary">— No one selected</Typography>
                            ) : (
                              <List dense sx={{ mt: 0.5 }}>
                                {opt.choosers.map(c => (
                                  <ListItem key={`${q.qId}_${oi}_${c.uid}`} disableGutters>
                                    <ListItemText
                                      primary={
                                        <Box sx={{ display:'inline-flex', alignItems:'center', gap: 1 }}>
                                          <span>{c.name}</span>
                                          {c.selectedCorrect
                                            ? <Chip size="small" label="✓" color="success" />
                                            : <Chip size="small" label="✕" color="error" variant="outlined" />
                                          }
                                        </Box>
                                      }
                                      secondary={c.email}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            )}
                          </Box>
                        );
                      })}
                    </Paper>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* ===== QUIZ DISTRIBUTION ===== */}
          {isQuiz && quizResults && quizResults.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Questionnaire Responses</Typography>
              {quizResults.map((q, i) => (
                <Paper key={q.id} sx={{ p: 2, mb: 2 }}>
                  <Typography sx={{ fontWeight: 600, mb: 1 }}>
                    Q{i + 1}. {q.label}
                  </Typography>

                  {q.type === 'MCQ' && (
                    (q.options || []).map((opt, j) => {
                      const count = q.counts?.[j] || 0;
                      const percent = q.total ? Math.round((count / q.total) * 100) : 0;
                      return (
                        <Box key={j} sx={{ mb: 0.75 }}>
                          <Typography variant="body2">
                            {opt || `Option ${j + 1}`} — {count} ({percent}%)
                          </Typography>
                          <Box sx={{ height: 6, width: `${percent}%`, bgcolor: 'primary.main', borderRadius: 3 }} />
                        </Box>
                      );
                    })
                  )}

                  {q.type === 'STAR' && (
                    <Typography>
                      ⭐ Average: {q.avg?.toFixed(3) || '—'} ({q.count || 0} responses)
                    </Typography>
                  )}

                  {q.type === 'TEXT' && (
                    q.texts?.length ? (
                      <List dense>
                        {q.texts.map((ans, idx) => (
                          <ListItem key={idx} disableGutters>
                            <ListItemText primary={ans} />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography color="text.secondary">No responses yet.</Typography>
                    )
                  )}
                </Paper>
              ))}
            </Box>
          )}

          {/* ===== PEER REVIEW: ELIGIBILITY in TABS ===== */}
          {!isQuiz && hasAnyPeerData && (
            <>
              <Tabs
                value={eligibilityTab}
                onChange={(_, v) => setEligibilityTab(v)}
                sx={{ mb: 2 }}
              >
                <Tab label={`Eligible (${eligibleRows.length})`} />
                <Tab label={`Ineligible (${ineligibleRows.length})`} />
              </Tabs>

              {eligibilityTab === 0 && (
                <>
                  {eligibleRows.length === 0 ? (
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                      No one met the eligibility criteria yet (each attribute ≥ 3.0, performance ≥ 3.0 and attendance+micro ≥ 3.0).
                    </Typography>
                  ) : (
                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', mb: 3 }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign:'left', padding:'8px 6px' }}>Rank</th>
                          <th style={{ textAlign:'left', padding:'8px 6px' }}>Employee</th>
                          {starLabels.map(lbl => (
                            <th key={lbl} style={{ textAlign:'left', padding:'8px 6px' }}>{lbl}</th>
                          ))}
                          <th style={{ textAlign:'left', padding:'8px 6px' }}>Attr ★ (0–5)</th>
                          <th style={{ textAlign:'left', padding:'8px 6px' }}>Micro (0–1)</th>
                          <th style={{ textAlign:'left', padding:'8px 6px' }}>Attendance (0–4)</th>
                          <th style={{ textAlign:'left', padding:'8px 6px' }}>Performance (0–5)</th>
                          <th style={{ textAlign:'left', padding:'8px 6px' }}>Total (0–15)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {eligibleRows.map((row, i) => (
                          <tr key={row.targetUid}>
                            <td style={{ padding:'8px 6px', width: 70 }}>
                              <Chip label={`#${i + 1}`} size="small" />
                            </td>
                            <td style={{ padding:'8px 6px' }}>
                              <Typography sx={{ fontWeight: 600 }}>{row.name}</Typography>
                              {row.email && (
                                <Typography variant="caption" color="text.secondary">
                                  {row.email}
                                </Typography>
                              )}
                            </td>

                            {starLabels.map(lbl => (
                              <td key={lbl} style={{ padding:'8px 6px' }}>
                                {row.perQAvg?.[lbl] != null ? Number(row.perQAvg[lbl]).toFixed(3) : '—'}
                              </td>
                            ))}

                            <td style={{ padding:'8px 6px' }}>
                              <Chip color={row.attrPts >= 3 ? 'success' : 'warning'} label={row.attrPts.toFixed(3)} size="small" />
                            </td>
                            <td style={{ padding:'8px 6px' }}>
                              <Chip label={row.microPts.toFixed(3)} size="small" />
                            </td>
                            <td style={{ padding:'8px 6px' }}>
                              <Chip label={row.attendancePts.toFixed(3)} size="small" />
                            </td>
                            <td style={{ padding:'8px 6px' }}>
                              <Chip label={row.performancePts.toFixed(3)} size="small" />
                            </td>
                            <td style={{ padding:'8px 6px' }}>
                              <Chip color="primary" label={row.total15.toFixed(3)} size="small" sx={{ fontWeight: 700 }} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Box>
                  )}
                </>
              )}

              {eligibilityTab === 1 && (
                <>
                  {ineligibleRows.length === 0 ? (
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                      No ineligible candidates.
                    </Typography>
                  ) : (
                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', mb: 3 }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign:'left', padding:'8px 6px' }}>Employee</th>
                          {starLabels.map(lbl => (
                            <th key={lbl} style={{ textAlign:'left', padding:'8px 6px' }}>{lbl}</th>
                          ))}
                          <th style={{ textAlign:'left', padding:'8px 6px' }}>Attr ★ (0–5)</th>
                          <th style={{ textAlign:'left', padding:'8px 6px' }}>Micro (0–1)</th>
                          <th style={{ textAlign:'left', padding:'8px 6px' }}>Attendance (0–4)</th>
                          <th style={{ textAlign:'left', padding:'8px 6px' }}>Performance (0–5)</th>
                          <th style={{ textAlign:'left', padding:'8px 6px' }}>Total (0–15)</th>
                          <th style={{ textAlign:'left', padding:'8px 6px' }}>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ineligibleRows.map((row) => {
                          const reasons = [];
                          if (!row.passAttributes) {
                            reasons.push(`Attributes < 3 in: ${row.failedAttributes.join(', ')}`);
                          }
                          if (!row.passLast5) {
                            reasons.push(`Attendance + Micro < 3.0 (got ${row.last5.toFixed(3)})`);
                          }
                          if (!row.passPerformance) {
                            reasons.push(`Performance < 3.0 (got ${row.performancePts.toFixed(3)})`);
                          }
                          return (
                            <tr key={row.targetUid}>
                              <td style={{ padding:'8px 6px' }}>
                                <Typography sx={{ fontWeight: 600 }}>{row.name}</Typography>
                                {row.email && (
                                  <Typography variant="caption" color="text.secondary">
                                    {row.email}
                                  </Typography>
                                )}
                              </td>

                              {starLabels.map(lbl => (
                                <td key={lbl} style={{ padding:'8px 6px' }}>
                                  {row.perQAvg?.[lbl] != null ? Number(row.perQAvg[lbl]).toFixed(3) : '—'}
                                </td>
                              ))}

                              <td style={{ padding:'8px 6px' }}>
                                <Chip color={row.attrPts >= 3 ? 'success' : 'error'} label={row.attrPts.toFixed(3)} size="small" />
                              </td>
                              <td style={{ padding:'8px 6px' }}>
                                <Chip label={row.microPts.toFixed(3)} size="small" />
                              </td>
                              <td style={{ padding:'8px 6px' }}>
                                <Chip label={row.attendancePts.toFixed(3)} size="small" />
                              </td>
                              <td style={{ padding:'8px 6px' }}>
                                <Chip label={row.performancePts.toFixed(3)} size="small" />
                              </td>
                              <td style={{ padding:'8px 6px' }}>
                                <Chip label={row.total15.toFixed(3)} size="small" />
                              </td>
                              <td style={{ padding:'8px 6px' }}>
                                {reasons.map((r, i) => (
                                  <Chip key={i} label={r} color="warning" size="small" sx={{ mr: .5, mb: .5 }} />
                                ))}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Box>
                  )}
                </>
              )}
            </>
          )}

          {/* ===== PEER REVIEW – per-employee table ===== */}
          {!isQuiz && (
            <>
              {!summary || summary.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography>No ratings found yet.</Typography>
                  <Typography variant="caption">
                    (STAR ratings and micro decimals from <code>/eventStatusByUser/{'{eventId}'}/performance</code>.)
                  </Typography>
                </Box>
              ) : (
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign:'left', padding:'8px 6px' }}>Rank</th>
                      <th style={{ textAlign:'left', padding:'8px 6px' }}>Employee</th>
                      {starLabels.map(lbl => (
                        <th key={lbl} style={{ textAlign:'left', padding:'8px 6px' }}>{lbl}</th>
                      ))}
                      <th style={{ textAlign:'left', padding:'8px 6px' }}>Overall ★</th>
                      <th style={{ textAlign:'left', padding:'8px 6px' }}>Micro (0–1)</th>
                      <th style={{ textAlign:'left', padding:'8px 6px' }}># Reviewers</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.map((row, i) => (
                      <tr key={row.targetUid}>
                        <td style={{ padding:'8px 6px', width: 70 }}>
                          <Chip label={`#${i + 1}`} size="small" />
                        </td>
                        <td style={{ padding:'8px 6px' }}>
                          <Typography sx={{ fontWeight: 600 }}>{row.name}</Typography>
                          {row.email && (
                            <Typography variant="caption" color="text.secondary">
                              {row.email}
                            </Typography>
                          )}
                        </td>

                        {starLabels.map(lbl => (
                          <td key={lbl} style={{ padding:'8px 6px' }}>
                            {row.perQAvg?.[lbl] != null ? Number(row.perQAvg[lbl]).toFixed(3) : '—'}
                          </td>
                        ))}

                        <td style={{ padding:'8px 6px' }}>
                          <Chip
                            color="primary"
                            label={row.overall.toFixed(3)}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        </td>

                        <td style={{ padding:'8px 6px' }}>
                          {row.microVotes > 0
                            ? <Chip size="small" label={`${row.microAvg.toFixed(3)} (${row.microVotes})`} />
                            : <Typography variant="body2" color="text.secondary">—</Typography>
                          }
                        </td>

                        <td style={{ padding:'8px 6px' }}>{row.reviewers}</td>
                      </tr>
                    ))}
                  </tbody>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />
              <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb: 1, gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ mb: { xs: 1, sm: 0 } }}>
                  Ratings Given (by Reviewer)
                </Typography>
                <TextField
                  size="small"
                  placeholder="Search reviewer or target…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 260 }}
                />
              </Box>

              {filteredReviewerSummaries.length === 0 ? (
                <Typography color="text.secondary">
                  {search ? 'No matches.' : 'No reviewer submissions yet.'}
                </Typography>
              ) : (
                <Box>
                  {filteredReviewerSummaries.map((rev) => {
                    const allRows = givenByReviewer[rev.reviewerUid] || [];
                    const q = search.trim().toLowerCase();
                    const rowsToShow = q
                      ? allRows.filter(r =>
                          rev.name.toLowerCase().includes(q) ||
                          (rev.email || '').toLowerCase().includes(q) ||
                          (r.targetName || '').toLowerCase().includes(q)
                        )
                      : allRows;
                    const isOpen = expanded.has(rev.reviewerUid);

                    return (
                      <Paper
                        key={rev.reviewerUid}
                        variant="outlined"
                        sx={{
                          mb: 2,
                          borderRadius: 3,
                          overflow: 'hidden',
                          WebkitMaskImage: '-webkit-radial-gradient(white, black)',
                          borderColor: 'divider',
                        }}
                      >
                        <Box
                          onClick={() => toggleExpanded(rev.reviewerUid)}
                          sx={{
                            p: 1.5,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            bgcolor: isOpen ? 'action.hover' : 'background.paper',
                            cursor: 'pointer',
                          }}
                        >
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{rev.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{rev.email || '—'}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Chip label={`${rev.count} rated`} size="small" />
                            <Chip label={`Avg ${rev.avgGiven.toFixed(3)}`} size="small" color="primary" />
                            <Tooltip title={isOpen ? 'Collapse' : 'Expand'}>
                              <IconButton size="small">
                                {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>

                        {isOpen && (
                          <Box
                            sx={{
                              bgcolor: 'background.paper',
                              borderTop: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            {rowsToShow.length === 0 ? (
                              <Typography color="text.secondary" sx={{ px: 1.5, py: 1 }}>
                                {search ? 'No matches for this reviewer.' : 'No detailed rows to display.'}
                              </Typography>
                            ) : (
                              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr>
                                      <th style={{ textAlign: 'left', padding: '8px 10px' }}>Target</th>
                                      {starLabels.map(lbl => (
                                        <th key={lbl} style={{ textAlign: 'left', padding: '8px 10px' }}>{lbl}</th>
                                      ))}
                                      <th style={{ textAlign: 'left', padding: '8px 10px' }}>Overall Given</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {rowsToShow.map(row => (
                                      <tr key={`${row.reviewerUid}_${row.targetUid}`}>
                                        <td style={{ padding: '8px 10px' }}>
                                          <Typography>{row.targetName}</Typography>
                                        </td>
                                        {starLabels.map(lbl => (
                                          <td key={lbl} style={{ padding: '8px 10px' }}>
                                            {row.perQ?.[lbl] != null ? Number(row.perQ[lbl]).toFixed(3) : '—'}
                                          </td>
                                        ))}
                                        <td style={{ padding: '8px 10px' }}>
                                          <Chip label={row.overallGiven.toFixed(3)} size="small" />
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Box>
                              </Box>
                            )}
                          </Box>
                        )}
                      </Paper>
                    );
                  })}
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm declare results dialog */}
      <Dialog open={confirmDeclareOpen} onClose={() => setConfirmDeclareOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Declare results?</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Are you sure you want to declare the results? This will be visible to all employees who participated in this event.
          </Typography>
          <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
            Make sure you’ve added attendance and performance points before declaring the results.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeclareOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={confirmWorking}
            onClick={async () => {
              try {
                setConfirmWorking(true);
                await update(ref(db, `/events/${event.id}/results`), {
                  declared: true,
                  declaredAt: Date.now(),
                });
                setConfirmDeclareOpen(false);
              } catch (err) {
                console.error('Failed to declare results', err);
              } finally {
                setConfirmWorking(false);
              }
            }}
          >
            Yes, declare
          </Button>
        </DialogActions>
      </Dialog>

      {!isQuiz && (
        <Dialog open={participationOpen} onClose={() => setParticipationOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Participation</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Reviewers are based on the event audience. “Partial” means the reviewer submitted but didn’t rate every target.
            </Typography>

            <Tabs
              value={participationTab}
              onChange={(_, v) => setParticipationTab(v)}
              sx={{ mb: 1 }}
            >
              <Tab label={`Reviewed (${reviewedList.length})`} />
              <Tab label={`Not Participated (${notParticipatedList.length})`} />
              <Tab
                label={
                  event?.peerReview?.enabled
                    ? `Partial (${partialList.length})`
                    : 'Partial (N/A)'
                }
                disabled={!event?.peerReview?.enabled}
              />
            </Tabs>

            {participationTab === 0 && (
              <List dense>
                {reviewedList.length === 0 ? (
                  <Typography color="text.secondary">No one has reviewed yet.</Typography>
                ) : reviewedList
                    .map(uid => ({ uid, ...infoOf(uid) }))
                    .sort((a,b) => a.name.localeCompare(b.name))
                    .map(r => (
                      <ListItem key={r.uid} disableGutters>
                        <ListItemText primary={r.name} secondary={r.email} />
                      </ListItem>
                    ))
                }
              </List>
            )}

            {participationTab === 1 && (
              <List dense>
                {notParticipatedList.length === 0 ? (
                  <Typography color="text.secondary">Everyone participated.</Typography>
                ) : notParticipatedList
                    .map(uid => ({ uid, ...infoOf(uid) }))
                    .sort((a,b) => a.name.localeCompare(b.name))
                    .map(r => (
                      <ListItem key={r.uid} disableGutters>
                        <ListItemText primary={r.name} secondary={r.email} />
                      </ListItem>
                    ))
                }
              </List>
            )}

            {participationTab === 2 && event?.peerReview?.enabled && (
              <List dense>
                {partialList.length === 0 ? (
                  <Typography color="text.secondary">No partial submissions.</Typography>
                ) : partialList
                    .map(uid => {
                      const ans = raw?.data?.[uid]?.answers || {};
                      const ratedCount = expectedTargets.filter(t => ans[t]).length;
                      const nameEmail = infoOf(uid);
                      return { uid, ratedCount, name: nameEmail.name, email: nameEmail.email };
                    })
                    .sort((a,b) => a.name.localeCompare(b.name))
                    .map(r => (
                      <ListItem key={r.uid} disableGutters>
                        <ListItemText
                          primary={r.name}
                          secondary={`${r.email || ''} • Rated ${r.ratedCount} of ${expectedTargets.length}`}
                        />
                      </ListItem>
                    ))
                }
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setParticipationOpen(false)} variant="contained">Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
